import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Flex, TextV2, TooltipV2, styled } from '@withjoy/joykit';
import { Hamburger } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { Link, useLocation } from '@react-router';
import { useTranslation } from '@shared/core/i18n';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useStickyNavBarOffsetData } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import { useWeddingCodeBannerOffset } from '@apps/guest/packages/layout-engine/components/WeddingCodeBanner';
import { useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { animationTransition } from '@shared/utils/animationTransition';
import { useScrollPastHero } from '@apps/guest/packages/layout-engine/layouts/hooks/useScrollPastHero';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { label3ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { PreviewDisabledTooltipContent } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/PreviewDisabledTooltipContent';
import { usePreviewIframeScale } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/usePreviewIframeScale';
import { applyUtmParametersToUrl } from '@shared/utils/utmParameters';
import type { StickyNavVariantProps } from './MobileTopBarNav.types';
import { useMobileTopBarNavMenu, useSetMobileTopBarNavOffset } from './MobileTopBarNavOffset.context';

const STICKY_NAV_HEIGHT_PX = 56;

const ScrollableNav = styled(Flex)`
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GlassmorphicBar = styled(Box)`
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
`;

const stripQuery = (url: string) => url.split('?')[0];

export const StickyNavVariant: React.FC<StickyNavVariantProps> = ({ pages, onLinkClick, onMenuClick, eventPageRoute, resolveNavUtm, isAdminPreview }) => {
  const { tq } = useTranslation('eventMenu');
  const { accentColor, pageFontColor } = useColorPaletteProvider();
  const { offset } = useStickyNavBarOffsetData();
  const { smartAppBannerHeight } = useSmartAppBannerProvider();
  const { weddingBannerHeight } = useWeddingCodeBannerOffset();
  const { isPastThreshold } = useScrollPastHero();
  const location = useLocation();
  const setMobileTopBarNavOffset = useSetMobileTopBarNavOffset();
  const { openMenu, isMenuOpen } = useMobileTopBarNavMenu();
  const previewScale = usePreviewIframeScale(isAdminPreview);

  const handleMenuClick = () => {
    openMenu();
    onMenuClick();
  };

  const top = Math.max(smartAppBannerHeight, offset) + weddingBannerHeight;
  const activeIndicatorColor = accentColor || 'currentColor';
  const navTextColor = pageFontColor || 'mono14';
  // Hide the bar while the menu drawer is open so the drawer sits flush at the top instead of
  // below a redundant strip; zeroing the offset (below) lets the drawer reclaim that 56px.
  const isBarVisible = isPastThreshold && !isMenuOpen;

  useEffect(() => {
    setMobileTopBarNavOffset(isBarVisible ? STICKY_NAV_HEIGHT_PX : 0);
    return () => setMobileTopBarNavOffset(0);
  }, [isBarVisible, setMobileTopBarNavOffset]);

  const activeLinkRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    const node = activeLinkRef.current;
    if (!node || typeof node.scrollIntoView !== 'function') return;
    node.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [location.pathname]);

  const linksWithActive = useMemo(
    () =>
      pages.map(page => {
        const navUtm = resolveNavUtm(page.pageSlug);
        const href = applyUtmParametersToUrl(eventPageRoute.goToPath(page.type, page.pageSlug), navUtm);
        // stripQuery keeps the active-pill match working now that the href carries UTM params.
        const isActive = stripQuery(href) === location.pathname;
        return { page, href, isActive, navUtm };
      }),
    [pages, eventPageRoute, resolveNavUtm, location.pathname],
  );

  return (
    <GlassmorphicBar
      position="fixed"
      top={pxToRem(top)}
      left={0}
      right={0}
      zIndex="modal"
      paddingY={pxToRem(8)}
      backgroundColor="rgba(247, 247, 247, 0.6)"
      borderBottom="1px solid rgba(255, 255, 255, 0.5)"
      opacity={isBarVisible ? 1 : 0}
      transform={isBarVisible ? 'translateY(0)' : 'translateY(-12px)'}
      pointerEvents={isBarVisible ? 'auto' : 'none'}
      transition={animationTransition('opacity', 'transform', { duration: '420ms' })}
      display={{ _: 'flex', sm2: 'none' }}
      alignItems="center"
      data-testid="mobile-top-bar-nav"
    >
      <Flex
        as="button"
        onClick={handleMenuClick}
        aria-label={tq`Menu`}
        flexShrink={0}
        alignItems="center"
        justifyContent="center"
        paddingLeft={pxToRem(16)}
        paddingRight={pxToRem(4)}
        border="none"
        background="transparent"
        cursor="pointer"
        color={navTextColor}
        // Nudge up ~2px to optically center the icon with the link text: the links carry a 2px
        // bottom border that lifts their glyphs within the centered row, which the icon lacks.
        transform="translateY(-2px)"
        data-testid="mobile-top-bar-nav-menu-button"
      >
        <Hamburger size={28} />
      </Flex>
      <ScrollableNav
        as="nav"
        gap={pxToRem(12)}
        alignItems="center"
        flexWrap="nowrap"
        overflowX="auto"
        overflowY="hidden"
        flex={1}
        minWidth={0}
        paddingRight={pxToRem(16)}
      >
        {linksWithActive.map(({ page, href, isActive, navUtm }) => {
          const isLegacyPage = page.pageSlug === 'moments';
          const isDisabledInPreview = isLegacyPage && isAdminPreview;

          const linkContent = (
            <GuestSiteTypographyOverride override={label3ToNavigationOverride}>
              <TextV2
                typographyVariant="label3"
                color={navTextColor}
                fontWeight={500}
                paddingX={pxToRem(8)}
                paddingY={pxToRem(6)}
                whiteSpace="nowrap"
                borderBottom="2px solid"
                borderColor={isActive ? activeIndicatorColor : 'transparent'}
                transition={animationTransition('border-color', { duration: '420ms' })}
                cursor={isDisabledInPreview ? 'default' : undefined}
              >
                {page.pageTitle}
              </TextV2>
            </GuestSiteTypographyOverride>
          );

          if (isDisabledInPreview) {
            return (
              <TooltipV2
                key={page.id}
                onMouseEnterDelay={500}
                content={
                  <PreviewDisabledTooltipContent
                    href={href}
                    previewScale={previewScale}
                    maxWidth={240}
                  />
                }
              >
                <Box style={{ flexShrink: 0 }}>{linkContent}</Box>
              </TooltipV2>
            );
          }

          return (
            <Link
              key={page.id}
              to={href}
              onClick={() => onLinkClick(page.pageSlug, navUtm)}
              style={{ textDecoration: 'none', flexShrink: 0 }}
              aria-current={isActive ? 'page' : undefined}
              ref={isActive ? activeLinkRef : undefined}
            >
              {linkContent}
            </Link>
          );
        })}
      </ScrollableNav>
    </GlassmorphicBar>
  );
};

StickyNavVariant.displayName = 'StickyNavVariant';
